import {
  getPushKey,
  registerPushDevice,
  urlBase64ToUint8Array,
} from '../../api/notifications-client';

/**
 * WEB PUSH ON THIS DEVICE — the probe and the opt-in, in one place.
 *
 * Two screens turn push on: Account › Notifications, and the resident portal's
 * "Get told when someone arrives" card. They run THIS flow rather than a copy
 * each, because every step of it is load-bearing (the user gesture, the key
 * fetched from the server, the self-scoped worker, userVisibleOnly) and a copy
 * is where one of those quietly goes missing. What each screen SAYS about the
 * outcome is its own business, so the outcome comes back as a word, never as
 * a toast.
 */

export const PUSH_SW_PATH = '/push-sw.js';

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'ready' | 'enabled';

/**
 * What can this BROWSER do? Separate from what the SERVER offers, and both
 * have to be true. iOS in particular only grants push to a PWA that has been
 * added to the home screen, so a Safari user on a tab legitimately sees
 * "unsupported" while everything server-side is fine.
 */
export const browserSupportsPush = (): boolean =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

interface ProbeOptions {
  /** Re-register an existing endpoint under the CURRENT session (see below).
   *  On by default; the portal's tip only needs to know whether to show. */
  reclaim?: boolean;
}

export const probePushState = async ({
  reclaim = true,
}: ProbeOptions = {}): Promise<PushState> => {
  if (!browserSupportsPush()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  try {
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SW_PATH);
    const existing = await registration?.pushManager.getSubscription();

    /* A SUBSCRIPTION IS A FACT ABOUT THE BROWSER, NOT ABOUT THE ACCOUNT.
     *
     * getSubscription() answers "does this browser have a push endpoint",
     * which says nothing about WHOSE endpoint it is — and the preferences
     * screen used to read it as "push is on for you" and then DISABLE the
     * button.
     *
     * On a shared front-desk tablet that is a privacy failure with a lock
     * on it. Concierge A turns push on and goes home; concierge B signs in
     * on the same tablet; A's parcel and visitor notifications — third-party
     * names and unit numbers, which the notification copy is deliberately
     * written around — keep painting on that tablet's lock screen, while B
     * is told push is already enabled and receives nothing. B cannot press
     * the one control that would fix it.
     *
     * Re-registering the endpoint under the CURRENT session is what makes
     * "enabled" true rather than merely plausible: the server's
     * lb_push_subscribe() re-assigns an existing endpoint row to the caller
     * (lobbero_be/src/CRUD/notifications.js), so this is idempotent for the
     * person who really does own it and corrective for everyone else. */
    if (existing && reclaim) {
      try {
        await registerPushDevice(existing);
      } catch {
        // The endpoint stays where it was. Reporting "enabled" would be the
        // original lie, so fall back to offering the button.
        return 'ready';
      }
    }

    return existing ? 'enabled' : 'ready';
  } catch {
    return 'ready';
  }
};

/**
 * How an opt-in ended. `dismissed` is the prompt closed without an answer,
 * which the browser lets the page ask again; `denied` is a block it does not.
 */
export type EnablePushResult =
  | 'enabled'
  | 'denied'
  | 'dismissed'
  | 'not-configured'
  | 'failed';

/**
 * Turn on push for THIS browser.
 *
 * Only ever from an explicit button, never on load, and that is not only
 * politeness: browsers permanently block a site that asks for notification
 * permission without a user gesture, and a permanent block cannot be undone
 * from inside the page.
 */
export const enablePushOnThisDevice = async (): Promise<EnablePushResult> => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return permission === 'denied' ? 'denied' : 'dismissed';
    }

    const key = await getPushKey();
    if (!key.enabled || !key.public_key) return 'not-configured';

    // Registered on demand rather than at boot. The app-wide service worker
    // was deliberately retired (it broke navigation), so this one is scoped
    // to itself and has NO fetch handler — it cannot intercept a request.
    const registration = await navigator.serviceWorker.register(PUSH_SW_PATH);
    await navigator.serviceWorker.ready;

    const subscription =
      (await registration.pushManager.getSubscription()) ||
      (await registration.pushManager.subscribe({
        // Non-negotiable in Chrome: a subscription that could receive a
        // silent push is refused outright.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key.public_key),
      }));

    await registerPushDevice(subscription);

    return 'enabled';
  } catch {
    return 'failed';
  }
};
